import apiRoutes from '@constants/apiRoutes';
import baseApi from '@services/api';
import { SelfServeDetailsByIdApiMap } from './types/SelfServeDetails';
import { UpdateSelfServeRequestStatusParams } from '../pages/Admin/SelfServeRequests/ModifyPermissions/types';
import { DataAccessProvider, SelfServeDataAccessStatus } from '@constants/constants';
import { GameRevenueDetailsDto } from '../pages/Dashboard/GettingStartedDashboards/types';

export interface SelfServeDetails {
  accessEmail?: string;
  category: string | null;
  gameId: string;
  genre: string | null;
  id: string;
  metadata: string;
  requestStatus: string;
  unrequestedDashboards?: any;
  appUrl: string;
  companyName?: string;
  companyAddress?: string;
  signingAuthorityName?: string;
  signingAuthorityEmail?: string;
  signingAuthorityTitle?: string;
  mmp: string;
}

export interface SelfServeDetailsApiMap {
  code: number;
  message: string;
  data: GameRequest[];
}

export interface GameRequest {
  id: string;
  gameId: string;
  category: string | null;
  genre: string | null;
  requestStatus: SelfServeDataAccessStatus;
  metadata: string;
  accessEmail: string;
  companyName: string | null;
  companyAddress: string | null;
  signingAuthorityName: string | null;
  signingAuthorityEmail: string | null;
  signingAuthorityTitle: string | null;
  mmp: DataAccessProvider;
  refreshRequired?: boolean;
  accountName?: string;
  iapMargin?: string;
  connectionDetails?: string;
  dataSourceMetadata?: string;
  attachmentId?: string;
  gameRevenueDetailsDto?: GameRevenueDetailsDto;
}

export interface SelfServeCategoriesAndGenreOptionsApiMap {
  code: number;
  message: string;
  data: {
    categories: {
      name: string;
      genres: string[];
    }[];
  };
}

export interface SelfServeUpdateCategoryAndGenrePayload {
  category: string;
  genre: string;
}

export interface SelfServeUpdateApplicationDetailsPayload {
  appStoreUrl?: string;
  playStoreUrl?: string;
}

export interface SelfServeUpdateNDADetailsPayload {
  companyName: string;
  companyAddress: string;
  signingAuthorityName: string;
  signingAuthorityTitle: string;
  signingAuthorityEmail: string;
}

interface IapInformation {
  displayText: string;
  value: string;
}

interface IaaInformation {
  id: string;
  name: string;
}

interface MMPConfig {
  iapInformation: IapInformation[];
  iaaInformation?: IaaInformation[];
}

export interface SelfServeMMPConfigResponse {
  code: number;
  data: Record<string, MMPConfig>;
  message: string;
}

export interface AddNewGamePayload {
  appStoreUrl?: string;
  playStoreUrl?: string;
  category: string;
  genre: string;
}

export interface UpdateSelfServePayload {
  mmp: DataAccessProvider | null;
  accountName: string | null;
  iapMargin: string | null;
  iaaIdList: string[] | null;
  connectionDetails: any;
  refreshRequired: boolean;
}

export interface CreatePortfolioPayload {
  dealName: string;
  email: string;
  category: string;
  genre: string;
  clientCode: string;
  companyId: string;
  gameIds: string[];
}

const selfServe = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    fetchSelfServeById: builder.query<SelfServeDetailsByIdApiMap, string>({
      query: (id) => apiRoutes.selfServeById(id),
    }),
    fetchSelfServeDetails: builder.query<SelfServeDetailsApiMap, void>({
      query: () => apiRoutes.selfServeDetails,
    }),
    updateCategoryAndGenre: builder.mutation<
      any,
      {
        selfServeId: string;
        payload: SelfServeUpdateCategoryAndGenrePayload;
      }
    >({
      query: ({ selfServeId, payload }) => ({
        url: apiRoutes.selfServeUpdateCategoryAndGenre(selfServeId),
        method: 'PUT',
        body: payload,
      }),
    }),
    updateApplicationDetails: builder.mutation<
      any,
      {
        selfServeId: string;
        payload: SelfServeUpdateApplicationDetailsPayload;
      }
    >({
      query: ({ selfServeId, payload }) => ({
        url: apiRoutes.selfServeUpdateApplicationDetails(selfServeId),
        method: 'PUT',
        body: payload,
      }),
    }),
    fetchDashboardRequests: builder.query<any, string>({
      query: (qps: string) =>
        qps
          ? `${apiRoutes.selfServeDashboardRequests}?${qps}`
          : apiRoutes.selfServeDashboardRequests,
    }),
    fetchSelfServeAtachments: builder.query<any, { selfServeId: string; attachmentId: string }>({
      query: ({ selfServeId, attachmentId }) =>
        apiRoutes.selfServeAtachments(selfServeId, attachmentId),
    }),
    selfServeDataAccess: builder.mutation<
      any,
      {
        accountName: string;
        selfServeId: string;
        mmp: string;
        iapMargin: string;
        iaaIdList?: string;
        connectionDetails?: any;
        refreshRequired: boolean;
      }
    >({
      query: ({
        accountName,
        selfServeId,
        mmp,
        iapMargin,
        iaaIdList,
        connectionDetails,
        refreshRequired,
      }) => {
        return {
          url: apiRoutes.selfServeDataAccess(selfServeId),
          method: 'POST',
          body: {
            accountName,
            mmp,
            iapMargin,
            iaaIdList,
            connectionDetails,
            refreshRequired,
          },
        };
      },
    }),
    selfServeRequestDashboard: builder.mutation<any, { selfServeId: string; dashboardId: string }>({
      query: ({ selfServeId, dashboardId }) => ({
        url: apiRoutes.selfServeRequestDashboard(selfServeId, dashboardId),
        method: 'POST',
      }),
    }),
    selfServeNdaDetails: builder.mutation<
      void,
      {
        selfServeId: string;
        payload: SelfServeUpdateNDADetailsPayload;
      }
    >({
      query: ({ selfServeId, payload }) => ({
        url: apiRoutes.selfServeNDADetails(selfServeId),
        method: 'POST',
        body: payload,
      }),
    }),
    fetchSelfServeRefreshConfig: builder.query<any, { gameId: string }>({
      query: ({ gameId }) => apiRoutes.selfServeRefreshConfig(gameId),
    }),
    selfServeRefreshConfig: builder.mutation<any, { gameId: string; refreshEnabled: boolean }>({
      query: ({ gameId, refreshEnabled }) => ({
        url: apiRoutes.selfServeRefreshConfig(gameId),
        method: 'PUT',
        body: { refreshEnabled },
      }),
    }),
    fetchSelfServeMMPConfig: builder.query<SelfServeMMPConfigResponse, void>({
      query: () => apiRoutes.selfServeMMPConfig,
    }),
    updateSelfServeRequestStatus: builder.mutation<
      any,
      {
        selfServeId: string;
        payload: UpdateSelfServeRequestStatusParams;
      }
    >({
      query: ({ selfServeId, payload }) => ({
        url: apiRoutes.updateSelfServeStatus(selfServeId),
        method: 'PUT',
        body: payload,
      }),
    }),
    uploadNda: builder.mutation({
      query: ({ selfServeId, nda }) => ({
        url: apiRoutes.uploadNDA(selfServeId),
        method: 'POST',
        body: nda,
        formData: true,
      }),
    }),
    toggleArchive: builder.mutation<any, string>({
      query: (selfServeId: string) => ({
        url: apiRoutes.toggleArchiveStatus(selfServeId),
        method: 'PUT',
      }),
    }),
    deleteSelfServe: builder.mutation<any, string>({
      query: (selfServeId: string) => ({
        url: apiRoutes.deleteSelfServe(selfServeId),
        method: 'DELETE',
      }),
    }),
    fetchSelfServeDataRequirementDocument: builder.query<any, void>({
      query: () => apiRoutes.selfServeDataRequirementDocument,
    }),
    selfServeCsvDownload: builder.query<
      any,
      {
        mmp: string[];
        status: string[];
        showArchived: boolean;
        portfolio: boolean;
      }
    >({
      query: ({ mmp, status, showArchived, portfolio }) => {
        const queryParams: string[] = [];

        if (Array.isArray(mmp) && mmp.length > 0) {
          queryParams.push(...mmp.map((item) => `mmp=${encodeURIComponent(item)}`));
        }
        queryParams.push(`sort=updatedAt`);
        queryParams.push(`sortOrder=DESC`);

        if (Array.isArray(status) && status.length > 0) {
          queryParams.push(...status.map((item) => `status=${encodeURIComponent(item)}`));
        }

        if (showArchived === false) {
          queryParams.push(`archived=${showArchived}`);
        }
        if (portfolio === false) {
          queryParams.push(`type=INDIVIDUAL`);
        }

        return apiRoutes.selfServeCsvDownload(queryParams.join('&'));
      },
    }),
    selfServeAdminAggregatedCohortDataFileUpload: builder.mutation<any, any>({
      query: (payload) => {
        const formData = new FormData();
        formData.append('data', payload.file);

        return {
          url: apiRoutes.selfServeAdminAggregatedCohortFileUpload,
          method: 'POST',
          body: formData,
        };
      },
    }),
    selfServeAggregatedCohortDataFileUpload: builder.mutation<any, any>({
      query: (payload) => {
        const formData = new FormData();
        formData.append('data', payload.file);

        return {
          url: apiRoutes.selfServeAggregatedCohortFileUpload(payload.selfServeId),
          method: 'POST',
          body: formData,
        };
      },
    }),
    selfServeAddGame: builder.mutation<any, AddNewGamePayload>({
      query: (payload) => ({
        url: apiRoutes.addGame,
        method: 'POST',
        body: payload,
      }),
    }),
    toggleMonthlyRefresh: builder.mutation<any, { gameId: string; refreshRequired: boolean }>({
      query: (payload) => ({
        url: apiRoutes.selfServeMonthlyRefresh,
        method: 'PUT',
        body: payload,
      }),
    }),
    createPortfolio: builder.mutation<any, any>({
      query: (payload: CreatePortfolioPayload) => ({
        url: apiRoutes.createPortfolio,
        method: 'POST',
        body: payload,
      }),
    }),
    getPortfolioDetails: builder.query<any, string>({
      query: (portfolioId: string) => apiRoutes.portfolioDetails(portfolioId),
    }),
    deletePortfolio: builder.mutation<any, string>({
      query: (portfolioId: string) => ({
        url: apiRoutes.deletePortfolio(portfolioId),
        method: 'DELETE',
      }),
    }),
    adminUpdateSelfServe: builder.mutation<
      any,
      { selfServeId: string; gameData: UpdateSelfServePayload }
    >({
      query: (payload) => ({
        url: apiRoutes.adminEditSelfServe(payload.selfServeId),
        method: 'PUT',
        body: payload.gameData,
      }),
    }),
  }),
});

export const {
  useLazyFetchSelfServeDetailsQuery,
  useUpdateCategoryAndGenreMutation,
  useUpdateApplicationDetailsMutation,
  useLazyFetchDashboardRequestsQuery,
  useFetchDashboardRequestsQuery,
  useSelfServeDataAccessMutation,
  useSelfServeRequestDashboardMutation,
  useSelfServeNdaDetailsMutation,
  useFetchSelfServeRefreshConfigQuery,
  useSelfServeRefreshConfigMutation,
  useLazyFetchSelfServeAtachmentsQuery,
  useLazyFetchSelfServeMMPConfigQuery,
  useLazyFetchSelfServeByIdQuery,
  useUpdateSelfServeRequestStatusMutation,
  useLazySelfServeCsvDownloadQuery,
  useUploadNdaMutation,
  useToggleArchiveMutation,
  useDeleteSelfServeMutation,
  useLazyFetchSelfServeDataRequirementDocumentQuery,
  useSelfServeAdminAggregatedCohortDataFileUploadMutation,
  useSelfServeAggregatedCohortDataFileUploadMutation,
  useSelfServeAddGameMutation,
  useToggleMonthlyRefreshMutation,
  useCreatePortfolioMutation,
  useDeletePortfolioMutation,
  useLazyGetPortfolioDetailsQuery,
  useAdminUpdateSelfServeMutation,
} = selfServe;
